/** Shallow breaking sets, attached to the seabed contours. This is a bounded
 * visual displacement (like the existing ocean swell); gameplay uses mean sea
 * level. It never changes river surfaces or creates offshore whitecaps. */
export const SHORE_SPAN = 256;
export const SHORE_RES = 128;
export const SHORE_STEP = SHORE_SPAN / SHORE_RES;
export const SHORE_CACHE_STEP = 16;

/** Slide a fixed lattice window, retaining overlapping samples exactly. */
export function fillShoreWindow(target: Uint16Array, previous: Uint16Array,
  shiftX: number, shiftY: number, sample: (i: number,j: number,channel:number)=>number,channels=1): number {
  const n=SHORE_RES+1;
  let sampled=0;
  for(let j=0;j<n;j++)for(let i=0;i<n;i++){
    const x=i+shiftX,y=j+shiftY;
    if(x>=0&&x<n&&y>=0&&y<n){for(let c=0;c<channels;c++)target[(j*n+i)*channels+c]=previous[(y*n+x)*channels+c];}
    else {for(let c=0;c<channels;c++)target[(j*n+i)*channels+c]=sample(i,j,c);sampled++;}
  }
  return sampled;
}

/** Signed depth is essential: clamping dry samples before interpolation moves
 * the shoreline onto land. Half floats retain centimetres in the surf zone. */
export const COASTAL_DEPTH_GLSL = /* glsl */ `
uniform sampler2D uShoreDepth;
uniform vec2 uShoreCenter;
uniform vec2 uShoreView;
uniform float uShoreReady;
float shoreWeight(vec2 p) {
  // Fade about the continuously moving viewer, not the snapped cache centre.
  // A 16 m cache slide therefore cannot pop depth, crests or foam at its rim.
  return uShoreReady*(1.0-smoothstep(88.0,116.0,max(abs(p.x-uShoreView.x),abs(p.y-uShoreView.y))));
}
float shoreDepth(vec2 p) {
  return texture2D(uShoreDepth,(p-uShoreCenter)/258.0+0.5).r;
}
float estuaryShelter(vec2 p,float coarse) {
  return mix(coarse,texture2D(uShoreDepth,(p-uShoreCenter)/258.0+0.5).g,shoreWeight(p));
}
vec2 shoreGradient(vec2 p) {
  return vec2(shoreDepth(p+vec2(2.0,0.0))-shoreDepth(p-vec2(2.0,0.0)),
              shoreDepth(p+vec2(0.0,2.0))-shoreDepth(p-vec2(0.0,2.0)))*0.25;
}
`;

export const COASTAL_SURF_GLSL = /* glsl */ `
float beachGrade(float grade){
  return smoothstep(0.015,0.045,grade)*(1.0-smoothstep(0.35,0.7,grade));
}
// Same wave/set clock as the arriving breaker. Fast uprush, a pause at the
// limit, then a longer drain. The wet footprint outlasts the moving foam edge.
// x: moving reach above mean sea; y: fading wet reach; z: cycle; w: beach gate.
vec4 beachRunup(vec2 p,float grade,float time){
  float along=sin(p.x*0.061+p.y*0.047);
  float cycle=fract(time*(80.0/600.0)+0.11*along+0.30);
  float set=0.58+0.42*sin(time*6.28318530718*(20.0/600.0)+p.x*0.019-p.y*0.027);
  float fingers=0.028*sin(p.x*0.83+p.y*0.47)+0.017*sin(p.x*1.91-p.y*0.71);
  float peak=(0.44+fingers)*(0.72+0.28*set);
  float rise=smoothstep(0.0,0.24,cycle);
  float drain=1.0-smoothstep(0.28,1.0,cycle);
  float reach=-0.08+(peak+0.08)*rise*drain;
  float wet=-0.08+(peak+0.08)*smoothstep(0.0,0.18,cycle)*(1.0-smoothstep(0.58,1.0,cycle));
  return vec4(reach,wet,cycle,beachGrade(grade));
}
// Phase increases with depth and time: every front advances into SHALLOWER
// water. The narrow leading face and broad rear face give a spilling crest.
// Alongshore modulation interrupts sets over tens of metres, not per pixel.
vec3 coastalSurf(vec2 p,float depth,float grade,float time) {
  float wet=smoothstep(0.025,0.55,depth);
  float shoal=wet*(1.0-smoothstep(2.4,4.4,depth));
  float beach=beachGrade(grade);
  float along=sin(p.x*0.061+p.y*0.047);
  float phase=fract(depth/1.75+time*(80.0/600.0)+0.11*along);
  float set=0.58+0.42*sin(time*6.28318530718*(20.0/600.0)+p.x*0.019-p.y*0.027);
  float broken=smoothstep(-0.5,0.55,sin(p.x*0.083-p.y*0.067)+0.35*sin(p.x*0.193+p.y*0.127));
  float rise=smoothstep(0.60,0.70,phase);
  float fall=1.0-smoothstep(0.70,0.98,phase);
  float height=0.55*shoal*beach*set*rise*fall;
  float rim=phase+0.009*sin(p.x*1.3-p.y*0.92);
  float crest=smoothstep(0.675,0.695,rim)*(1.0-smoothstep(0.712,0.738,rim));
  crest*=shoal*beach*set*broken;
  // Remnant foam trails the advancing lip, breaking into patches as it drains.
  float lace=sin(p.x*1.3+p.y*0.85+time*6.28318530718*(40.0/600.0))
           *sin(p.x*0.73-p.y*1.16-time*6.28318530718*(20.0/600.0));
  float wash=smoothstep(0.74,0.79,phase)*(1.0-smoothstep(0.83,1.0,phase));
  wash*=wet*(1.0-smoothstep(0.6,1.7,depth))*beach*set*broken*smoothstep(0.05,0.55,lace)*0.32;
  return vec3(height,crest,wash);
}
// Narrow, intermittent impact at a steep solid shore. Signed depth / slope
// estimates horizontal distance to the face, so a deep stack foot can react
// without coating its wall in the beach's climbing swash. No extra depth taps.
float cliffContactFoam(vec2 p,float depth,float grade,float time) {
  if(grade<0.55||depth<0.0||depth>24.0)return 0.0;
  float distance=depth/max(grade,0.55);
  if(distance>2.2)return 0.0;
  float along=sin(p.x*0.061+p.y*0.047);
  float phase=fract(distance/2.4+time*(80.0/600.0)+0.11*along);
  float pulse=smoothstep(0.52,0.67,phase)*(1.0-smoothstep(0.74,0.99,phase));
  float broken=smoothstep(-0.35,0.55,sin(p.x*1.13-p.y*0.87)+0.45*sin(p.x*2.1+p.y*1.37));
  float edge=(1.0-smoothstep(0.35,2.2,distance))*smoothstep(0.0,0.045,depth);
  return edge*pulse*broken*smoothstep(0.55,1.4,grade)*0.82;
}
`;
